import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Truck, UserPlus, Plus, Clock } from 'lucide-react';
import { useVehicles, useDeleteVehicle, Vehicle } from '@/hooks/useVehicles';
import { useDrivers, useDeleteDriver, Driver } from '@/hooks/useDrivers';
import { useInvitations, Invitation } from '@/hooks/useInvitations';
import { supabase } from '@/integrations/supabase/client';
import { AddVehicleDialog } from '@/components/fleet/AddVehicleDialog';
import { EditVehicleDialog } from '@/components/fleet/EditVehicleDialog';
import { EditDriverDialog } from '@/components/fleet/EditDriverDialog';
import { InviteDriverDialog } from '@/components/fleet/InviteDriverDialog';
import { VehicleCard } from '@/components/fleet/VehicleCard';
import { DriverCard } from '@/components/fleet/DriverCard';
import { InvitationCard } from '@/components/fleet/InvitationCard';
import { VehicleCardSkeleton, DriverCardSkeleton } from '@/components/LoadingSkeletons';
import { useTranslation } from 'react-i18next';

const Vehicles = () => {
  const { t } = useTranslation();
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [inviteDriverOpen, setInviteDriverOpen] = useState(false);
  const [editDriverOpen, setEditDriverOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();
  const { deleteVehicle } = useDeleteVehicle();
  const { drivers, isLoading: driversLoading } = useDrivers();
  const { deleteDriver } = useDeleteDriver();
  const { invitations, isLoading: invitationsLoading, deleteInvitation, sendInvitationEmail } = useInvitations();

  console.log('Vehicles page - drivers:', drivers);

  // All invitations sent by the company (pending, accepted or expired)
  const pendingInvitations = invitations;

  const handleResendEmail = async (invitation: Invitation) => {
    const { data: companyData } = await supabase
      .from('companies')
      .select('name')
      .eq('id', invitation.company_id)
      .single();
    
    const { data: roleData } = await supabase
      .from('roles')
      .select('name')
      .eq('id', invitation.role_id)
      .single();

    if (companyData && roleData) {
      await sendInvitationEmail(invitation, companyData.name, roleData.name);
    }
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setEditVehicleOpen(true);
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    try {
      await deleteVehicle(vehicleId);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleEditDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setEditDriverOpen(true);
  };

  const handleDeleteDriver = async (driverId: string) => {
    try {
      await deleteDriver(driverId);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            {t('fleet.title')}
          </h1>
          <p className="text-[#5A6B7A] mt-2 font-['Funnel_Sans']">
            {t('fleet.subtitle')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="vehicles" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-[#EBF4F8]">
          <TabsTrigger 
            value="vehicles" 
            className="data-[state=active]:bg-white data-[state=active]:text-[#0880AE] font-['Funnel_Sans'] font-medium"
          >
            <Truck size={18} className="mr-2" />
            {t('fleet.vehicles')}
          </TabsTrigger>
          <TabsTrigger 
            value="drivers"
            className="data-[state=active]:bg-white data-[state=active]:text-[#0880AE] font-['Funnel_Sans'] font-medium"
          >
            <UserPlus size={18} className="mr-2" />
            {t('fleet.drivers')}
          </TabsTrigger>
        </TabsList>

        {/* Tab Véhicules */}
        <TabsContent value="vehicles" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-medium text-[#37699E] font-['Lexend_Deca']">
              {t('fleet.myVehicles')} ({vehicles.length})
            </h2>
            <Button
              onClick={() => setAddVehicleOpen(true)}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg transition-colors duration-200"
            >
              <Plus size={18} className="mr-2" />
              {t('fleet.addVehicle')}
            </Button>
          </div>

          {vehiclesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <VehicleCardSkeleton key={i} />
              ))}
            </div>
          ) : vehicles.length === 0 ? (
            <Card className="border-2 border-dashed border-[#DBE2EA] rounded-xl">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Truck className="h-12 w-12 text-[#87ADD4] mb-4" />
                <p className="text-[#5A6B7A] text-center mb-4 font-['Funnel_Sans']">
                  {t('fleet.noVehicles')}
                </p>
                <Button
                  onClick={() => setAddVehicleOpen(true)}
                  variant="outline"
                  className="border-[#0880AE] text-[#0880AE] hover:bg-[#EBF4F8]"
                >
                  <Plus size={18} className="mr-2" />
                  {t('fleet.addFirstVehicle')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((vehicle) => (
                <VehicleCard 
                  key={vehicle.id} 
                  vehicle={vehicle}
                  onEdit={handleEditVehicle}
                  onDelete={handleDeleteVehicle}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab Chauffeurs */}
        <TabsContent value="drivers" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-medium text-[#37699E] font-['Lexend_Deca']">
              {t('fleet.myDrivers')} ({drivers.length})
            </h2>
            <Button
              onClick={() => setInviteDriverOpen(true)}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg transition-colors duration-200"
            >
              <UserPlus size={18} className="mr-2" />
              {t('fleet.inviteDriver')}
            </Button>
          </div>

          {driversLoading || invitationsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <DriverCardSkeleton key={i} />
              ))}
            </div>
          ) : drivers.length === 0 && pendingInvitations.length === 0 ? (
            <Card className="border-2 border-dashed border-[#DBE2EA] rounded-xl">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <UserPlus className="h-12 w-12 text-[#87ADD4] mb-4" />
                <p className="text-[#5A6B7A] text-center mb-4 font-['Funnel_Sans']">
                  {t('fleet.noDrivers')}
                </p>
                <Button
                  onClick={() => setInviteDriverOpen(true)}
                  variant="outline"
                  className="border-[#0880AE] text-[#0880AE] hover:bg-[#EBF4F8]"
                >
                  <UserPlus size={18} className="mr-2" />
                  {t('fleet.inviteFirstDriver')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Invitations en attente */}
              {pendingInvitations.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca'] flex items-center gap-2">
                    <Clock size={18} className="text-[#F2AC57]" />
                    {t('invite.sentInvitations')} ({pendingInvitations.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pendingInvitations.map((invitation) => (
                      <InvitationCard 
                        key={invitation.id} 
                        invitation={invitation}
                        onDelete={(id) => deleteInvitation.mutate(id)}
                        onResendEmail={handleResendEmail}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Chauffeurs actifs */}
              {drivers.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca'] flex items-center gap-2">
                    <UserPlus size={18} className="text-[#14A38B]" />
                    {t('fleet.activeDrivers')} ({drivers.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {drivers.map((driver) => (
                      <DriverCard 
                        key={driver.id} 
                        driver={driver}
                        onEdit={handleEditDriver}
                        onDelete={handleDeleteDriver}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddVehicleDialog open={addVehicleOpen} onOpenChange={setAddVehicleOpen} />
      {selectedVehicle && (
        <EditVehicleDialog 
          open={editVehicleOpen} 
          onOpenChange={setEditVehicleOpen}
          vehicle={selectedVehicle}
        />
      )}
      <InviteDriverDialog open={inviteDriverOpen} onOpenChange={setInviteDriverOpen} />
      {selectedDriver && (
        <EditDriverDialog 
          open={editDriverOpen} 
          onOpenChange={setEditDriverOpen}
          driver={selectedDriver}
        />
      )}
    </div>
  );
};

export default Vehicles;
